/**
 * AdminBlog — CMS-level blog editor
 *
 * Rich text editor requires TipTap. Install once:
 *   cd frontend && npm install @tiptap/react @tiptap/starter-kit \
 *     @tiptap/extension-image @tiptap/extension-link \
 *     @tiptap/extension-placeholder @tiptap/extension-underline \
 *     @tiptap/extension-text-align
 *
 * AI features require ANTHROPIC_API_KEY on the backend server.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sanitizeBlogHTML } from '../../utils/sanitize';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, Edit, Trash2, Search, Loader2, BookOpen, Eye, Heart,
  ArrowLeft, Save, Calendar, Clock, Globe, Upload, ImagePlus,
  Sparkles, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Link2, AlignLeft, AlignCenter, AlignRight,
  Code, Quote, Undo2, Redo2, X,
  BarChart2, ThumbsUp, MessageSquare, User, Settings2, Pencil,
  RotateCcw,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';
import CategorySelectWithAdd from '../../components/admin/CategorySelectWithAdd';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

const generateSlugLocal = (title) =>
  title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim('-');

const wordCount = (html) => {
  const text = html.replace(/<[^>]+>/g, ' ');
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const uploadFile = async (file, toast) => {
  const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
  if (!allowed.includes(file.type)) {
    toast({ title: 'Error', description: 'Use PNG, JPG, GIF, WebP or SVG', variant: 'destructive' });
    return null;
  }
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/admin/content/assets/upload`, {
    method: 'POST', headers: authHeaders(), body: formData,
  });
  if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
  const asset = await res.json();
  return asset.url?.startsWith('http') ? asset.url : `${API_URL}${asset.url}`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Featured Image Uploader ───────────────────────────────────────────────────

const FEATURED_ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const FEATURED_MAX_BYTES = 10 * 1024 * 1024;

const FeaturedImageUploader = ({ value, onChange, onUpload, uploading, storageMessage = null }) => {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validate = (file) => {
    if (!FEATURED_ALLOWED.includes(file.type)) {
      toast({ title: 'Unsupported file type', description: 'Use JPG, PNG, or WEBP.', variant: 'destructive' });
      return false;
    }
    if (file.size > FEATURED_MAX_BYTES) {
      toast({ title: 'File too large', description: 'Max size is 10 MB.', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleFile = async (file) => {
    if (!file || !validate(file)) return;
    const url = await onUpload(file);
    if (url) onChange(url);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; handleFile(f); e.target.value = ''; }}
      />

      {value ? (
        <div className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-video">
          <img src={value} alt="Featured" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={openPicker}
              disabled={uploading}
              className="bg-white/95 hover:bg-white text-gray-800"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
              Replace
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => onChange('')}
              disabled={uploading}
              className="bg-white/95 hover:bg-white text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-1.5" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={uploading}
          className={`w-full aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors text-center px-4 ${
            dragOver
              ? 'border-purple-500 bg-purple-50 text-purple-700'
              : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-purple-300 hover:bg-purple-50/40 hover:text-purple-600'
          } disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {uploading ? (
            <Loader2 className="w-7 h-7 animate-spin" />
          ) : (
            <ImagePlus className="w-7 h-7" />
          )}
          <div>
            <p className="text-sm font-medium">{uploading ? 'Uploading…' : 'Add a featured image'}</p>
            <p className="text-xs text-gray-400 mt-0.5">Drag & drop or click to upload · JPG, PNG, WEBP · max 10 MB</p>
          </div>
        </button>
      )}

      <p className="text-xs text-gray-400 mt-2">
        Recommended <span className="text-gray-500 font-medium">1200 × 675 px</span> · Minimum <span className="text-gray-500 font-medium">800 × 450 px</span>
      </p>

      {storageMessage && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          {storageMessage}
        </p>
      )}
    </div>
  );
};

// ── Rich Text Editor ──────────────────────────────────────────────────────────

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={`p-1.5 rounded transition-colors ${active ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'} disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <div className="w-px h-5 bg-gray-200 mx-0.5" />;

const RichTextEditor = ({ value, onChange, onImageInsert, uploading }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapImage.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg max-w-full my-4' } }),
      TiptapLink.configure({ openOnClick: false, HTMLAttributes: { class: 'text-purple-600 underline' } }),
      Placeholder.configure({ placeholder: 'Start writing your post…' }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: 'prose prose-gray max-w-none outline-none min-h-[420px] px-5 py-4 text-gray-800' },
    },
  });

  // Sync external value changes (e.g. AI apply)
  const lastExternal = useRef(value);
  useEffect(() => {
    if (editor && value !== lastExternal.current) {
      const cur = editor.getHTML();
      if (cur !== value) editor.commands.setContent(value || '', false);
      lastExternal.current = value;
    }
  }, [editor, value]);

  if (!editor) return null;

  const imageInputRef = useRef(null);
  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await onImageInsert(file);
    if (url) editor.chain().focus().setImage({ src: url }).run();
    e.target.value = '';
  };

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
        <ToolbarButton title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title="Heading 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}><Heading1 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 className="w-4 h-4" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title="Align left" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })}><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Align center" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })}><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Align right" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })}><AlignRight className="w-4 h-4" /></ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title="Link" onClick={setLink} active={editor.isActive('link')}><Link2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}><Quote className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Code block" onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')}><Code className="w-4 h-4" /></ToolbarButton>
        <ToolbarDivider />
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
        <ToolbarButton title="Insert image" onClick={() => imageInputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 className="w-4 h-4" /></ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

// ── AI Panel ──────────────────────────────────────────────────────────────────

const AIPanel = ({ post, onApply, onApplySEO }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');

  const run = async (action) => {
    if (!post.title && action === 'generate') {
      toast({ title: 'Add a title first', variant: 'destructive' }); return;
    }
    setLoading(true); setResult(''); setActiveAction(action);
    try {
      const res = await fetch(`${API_URL}/api/admin/content/blog/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          action,
          context: action === 'generate' ? post.title : post.content,
          prompt: customPrompt,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'AI request failed');
      if (action === 'seo' && data.seo) {
        onApplySEO(data.seo);
        toast({ title: 'SEO fields updated' });
      } else {
        setResult(data.result || '');
      }
    } catch (e) {
      toast({ title: 'AI Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open && (
        <div className="mb-3 w-80 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4" />
              <span className="font-semibold text-sm">AI Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-3 space-y-2">
            <Textarea
              placeholder="Optional: add specific instructions…"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={2}
              className="text-xs resize-none"
            />
            {[
              { action: 'generate', label: 'Generate post from title', icon: Pencil },
              { action: 'improve', label: 'Improve writing', icon: Sparkles },
              { action: 'seo', label: 'Auto-fill SEO fields', icon: Globe },
            ].map(({ action, label, icon: Icon }) => (
              <button
                key={action}
                onClick={() => run(action)}
                disabled={loading}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-purple-50 hover:text-purple-700 text-sm text-left transition-colors disabled:opacity-50"
              >
                {loading && activeAction === action
                  ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  : <Icon className="w-4 h-4 flex-shrink-0" />
                }
                {label}
              </button>
            ))}
            {result && (
              <div className="mt-2 space-y-2">
                <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-600 line-clamp-6" dangerouslySetInnerHTML={{ __html: sanitizeBlogHTML(result) }} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 h-7 text-xs bg-purple-600 hover:bg-purple-700" onClick={() => { onApply(result); setResult(''); }}>
                    Apply to editor
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setResult('')}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all text-sm font-medium"
      >
        <Sparkles className="w-4 h-4" />
        AI Write
      </button>
    </div>
  );
};

// ── SEO Tab ───────────────────────────────────────────────────────────────────

const SEOTab = ({ post, onChange }) => {
  const previewTitle = post.meta_title || post.title || 'Post Title';
  const previewDesc = post.meta_description || post.excerpt || 'Post description will appear here in search results.';
  const previewUrl = `yourdomain.com/blog/${post.slug || 'post-slug'}`;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Meta Title <span className="text-gray-400 font-normal">({(post.meta_title || '').length}/60)</span></Label>
          <Input
            value={post.meta_title || ''}
            onChange={(e) => onChange({ meta_title: e.target.value })}
            placeholder={post.title || 'Enter meta title…'}
            maxLength={60}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-sm font-medium">Meta Description <span className="text-gray-400 font-normal">({(post.meta_description || '').length}/160)</span></Label>
          <Textarea
            value={post.meta_description || ''}
            onChange={(e) => onChange({ meta_description: e.target.value })}
            placeholder={post.excerpt || 'Enter meta description…'}
            maxLength={160}
            rows={3}
            className="mt-1.5 resize-none"
          />
        </div>
        <div>
          <Label className="text-sm font-medium">URL Slug</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              value={post.slug || ''}
              onChange={(e) => onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
              placeholder="post-url-slug"
              className="flex-1 font-mono text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange({ slug: generateSlugLocal(post.title || '') })}
              disabled={!post.title}
            >
              Auto
            </Button>
          </div>
        </div>
      </div>

      {/* Google SERP preview */}
      <div>
        <Label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Search Preview</Label>
        <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 space-y-1">
          <p className="text-[13px] text-gray-500">{previewUrl}</p>
          <p className="text-[17px] text-blue-700 font-medium leading-snug hover:underline cursor-pointer">{previewTitle}</p>
          <p className="text-[13px] text-gray-600 leading-relaxed">{previewDesc}</p>
        </div>
      </div>

      {/* Score indicators */}
      {[
        { label: 'Title', len: (post.meta_title || '').length, min: 30, max: 60 },
        { label: 'Description', len: (post.meta_description || '').length, min: 80, max: 160 },
      ].map(({ label, len, min, max }) => {
        const ok = len >= min && len <= max;
        const warn = len > 0 && len < min;
        return (
          <div key={label} className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ok ? 'bg-green-500' : warn ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <span className="text-xs text-gray-500">
              {label}: {ok ? 'Good length' : warn ? `Too short (${len}/${min})` : len === 0 ? 'Missing' : `Too long (${len}/${max})`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ── Author Tab ────────────────────────────────────────────────────────────────

const AuthorTab = ({ post, onChange, onAvatarUpload }) => {
  const avatarInputRef = useRef(null);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="relative">
          {post.author_avatar
            ? <img src={post.author_avatar} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-gray-200" />
            : <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center"><User className="w-7 h-7 text-white" /></div>
          }
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50"
          >
            <Upload className="w-3 h-3 text-gray-500" />
          </button>
        </div>
        <div className="flex-1">
          <Label className="text-sm font-medium">Display Name</Label>
          <Input
            value={post.author_name_override || ''}
            onChange={(e) => onChange({ author_name_override: e.target.value })}
            placeholder="Override author name (leave blank to use account name)"
            className="mt-1"
          />
        </div>
      </div>
      <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onAvatarUpload(e.target.files?.[0])} />
      <div>
        <Label className="text-sm font-medium">Author Bio</Label>
        <Textarea
          value={post.author_bio || ''}
          onChange={(e) => onChange({ author_bio: e.target.value })}
          placeholder="A short bio shown below the post…"
          rows={3}
          className="mt-1.5 resize-none"
        />
      </div>
    </div>
  );
};

// ── Settings Tab ──────────────────────────────────────────────────────────────

const SettingsTab = ({ post, onChange, categories }) => {
  return (
    <div className="space-y-5">
      <CategorySelectWithAdd
        label="Category"
        type="blog"
        required
        categories={categories}
        value={post.category_id}
        onValueChange={(v) => onChange({ category_id: v })}
        onCategoryCreated={(cat) => categories.push(cat)}
        placeholder="Select category"
      />

      <div>
        <Label className="text-sm font-medium">Tags</Label>
        <Input
          value={Array.isArray(post.tags) ? post.tags.join(', ') : (post.tags || '')}
          onChange={(e) => onChange({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
          placeholder="design, tutorial, tips"
          className="mt-1.5"
        />
        <p className="text-xs text-gray-400 mt-1">Comma-separated</p>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <input
          id="featured-toggle"
          type="checkbox"
          checked={!!post.is_featured}
          onChange={(e) => onChange({ is_featured: e.target.checked })}
          className="rounded border-gray-300 text-purple-600"
        />
        <label htmlFor="featured-toggle" className="text-sm cursor-pointer select-none">Mark as Featured Post</label>
      </div>

      {/* Publish scheduling */}
      <div className="rounded-xl border border-gray-200 p-4 space-y-3">
        <Label className="text-sm font-medium">Publish Settings</Label>
        <Select value={post.status || 'draft'} onValueChange={(v) => onChange({ status: v })}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft — not visible</SelectItem>
            <SelectItem value="published">Published — live now</SelectItem>
            <SelectItem value="scheduled">Scheduled — publish at date</SelectItem>
          </SelectContent>
        </Select>
        {post.status === 'scheduled' && (
          <div>
            <Label className="text-xs text-gray-500">Publish date & time</Label>
            <Input
              type="datetime-local"
              value={post.scheduled_at ? post.scheduled_at.replace('Z', '').substring(0, 16) : ''}
              onChange={(e) => onChange({ scheduled_at: new Date(e.target.value).toISOString() })}
              className="mt-1 h-9 text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Analytics Tab ─────────────────────────────────────────────────────────────

const AnalyticsTab = ({ postId }) => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!postId || postId === 'new') { setLoading(false); return; }
    fetch(`${API_URL}/api/admin/content/blog/${postId}/analytics`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStats(data); })
      .catch(() => toast({ title: 'Could not load analytics', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>;
  if (!stats) return <p className="text-gray-500 text-sm">Analytics available after publishing.</p>;

  const metrics = [
    { icon: Eye, label: 'Total Views', value: stats.views?.toLocaleString() || '0', color: 'text-blue-600 bg-blue-50' },
    { icon: ThumbsUp, label: 'Likes', value: stats.likes?.toLocaleString() || '0', color: 'text-rose-600 bg-rose-50' },
    { icon: MessageSquare, label: 'Comments', value: stats.comments?.toLocaleString() || '0', color: 'text-amber-600 bg-amber-50' },
    { icon: Clock, label: 'Read Time', value: `${stats.read_time || 0} min`, color: 'text-green-600 bg-green-50' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        {metrics.map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
              <p className="text-xl font-bold text-gray-800">{value}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-gray-100 p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Created</span><span>{formatDate(stats.created_at)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Published</span><span>{stats.published_at ? formatDate(stats.published_at) : '—'}</span></div>
      </div>
    </div>
  );
};

// ── CTA Block Editor ──────────────────────────────────────────────────────────

const CTABlockEditor = ({ blocks, onChange }) => {
  const add = () => onChange([...blocks, { id: Date.now().toString(), type: 'button', text: 'Click here', url: '', button_text: 'Get Started', bg: '#6366f1' }]);
  const update = (idx, patch) => onChange(blocks.map((b, i) => i === idx ? { ...b, ...patch } : b));
  const remove = (idx) => onChange(blocks.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">CTA Blocks</Label>
        <Button type="button" variant="outline" size="sm" onClick={add} className="h-7 text-xs">
          <Plus className="w-3 h-3 mr-1" /> Add CTA
        </Button>
      </div>
      {blocks.length === 0 && (
        <p className="text-xs text-gray-400 py-2">No CTA blocks. Add a call-to-action button to embed in your post.</p>
      )}
      {blocks.map((block, idx) => (
        <div key={block.id} className="rounded-lg border border-gray-200 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">CTA {idx + 1}</span>
            <button type="button" onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
          </div>
          <Input placeholder="Headline text" value={block.text} onChange={(e) => update(idx, { text: e.target.value })} className="h-8 text-sm" />
          <div className="flex gap-2">
            <Input placeholder="Button label" value={block.button_text} onChange={(e) => update(idx, { button_text: e.target.value })} className="h-8 text-sm flex-1" />
            <Input placeholder="URL" value={block.url} onChange={(e) => update(idx, { url: e.target.value })} className="h-8 text-sm flex-1" />
          </div>
          {/* Mini preview */}
          <div className="rounded-lg p-3 text-center" style={{ background: block.bg + '18', border: `1px solid ${block.bg}40` }}>
            <p className="text-xs font-medium text-gray-700 mb-1.5">{block.text || 'Your headline'}</p>
            <a href={block.url || '#'} target="_blank" rel="noreferrer" className="inline-block text-xs font-semibold text-white px-4 py-1.5 rounded-lg transition-opacity hover:opacity-90" style={{ background: block.bg }}>
              {block.button_text || 'Get Started'}
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Editor ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'writing', label: 'Writing', icon: Pencil },
  { id: 'seo', label: 'SEO', icon: Globe },
  { id: 'author', label: 'Author', icon: User },
  { id: 'settings', label: 'Settings', icon: Settings2 },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
];

const BLANK_POST = {
  title: '', excerpt: '', content: '',
  thumbnail: '', category_id: '', tags: [],
  is_featured: false, is_published: false, read_time: 5,
  meta_title: '', meta_description: '', slug: '', status: 'draft',
  scheduled_at: '', author_bio: '', author_avatar: '', author_name_override: '',
  cta_blocks: [],
};

const AdminBlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('writing');
  const [post, setPost] = useState(BLANK_POST);
  const [storageStatus, setStorageStatus] = useState({ image_upload_available: true, message: null });

  const patch = useCallback((changes) => setPost(p => ({ ...p, ...changes })), []);

  // ── Data fetch ──
  useEffect(() => {
    const load = async () => {
      const [catRes, postRes, storageRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/content/categories?type=blog`, { headers: authHeaders() }),
        !isNew ? fetch(`${API_URL}/api/admin/content/blog/${id}`, { headers: authHeaders() }) : Promise.resolve(null),
        fetch(`${API_URL}/api/admin/content/storage-status`, { headers: authHeaders() }),
      ]);
      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(cats);
        if (isNew && cats.length > 0) patch({ category_id: cats[0].id });
      }
      if (postRes?.ok) {
        const data = await postRes.json();
        setPost({ ...BLANK_POST, ...data, tags: Array.isArray(data.tags) ? data.tags : [] });
      }
      if (storageRes.ok) {
        setStorageStatus(await storageRes.json());
      }
      setLoading(false);
    };
    load();
  }, [id, isNew, patch]);

  // ── Upload helpers ──
  const handleUpload = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file, toast);
      if (url) patch({ [field]: url });
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleEditorImageInsert = async (file) => {
    setUploading(true);
    try { return await uploadFile(file, toast); }
    catch (e) { toast({ title: 'Upload failed', description: e.message, variant: 'destructive' }); return null; }
    finally { setUploading(false); }
  };

  // ── Save ──
  const save = async (statusOverride) => {
    if (!post.title.trim()) { toast({ title: 'Title required', variant: 'destructive' }); return; }
    if (!post.category_id) { toast({ title: 'Select a category', variant: 'destructive' }); return; }
    const effectiveStatus = statusOverride || post.status;
    if (['published', 'scheduled'].includes(effectiveStatus) && !post.thumbnail) {
      toast({ title: 'Featured image required', description: 'Add a featured image before publishing or scheduling.', variant: 'destructive' });
      setActiveTab('writing');
      return;
    }
    setSaving(true);
    try {
      const wc = wordCount(post.content);
      const payload = {
        ...post,
        read_time: Math.max(1, Math.ceil(wc / 200)),
        slug: post.slug || generateSlugLocal(post.title),
        ...(statusOverride ? { status: statusOverride, is_published: statusOverride === 'published' } : {}),
      };
      const url = isNew ? `${API_URL}/api/admin/content/blog` : `${API_URL}/api/admin/content/blog/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      const saved = await res.json();
      toast({ title: statusOverride === 'published' ? 'Published!' : 'Saved', description: statusOverride === 'published' ? 'Your post is now live.' : 'Draft saved.' });
      if (isNew) navigate(`/admin/blog/${saved.id}`, { replace: true });
      else setPost({ ...BLANK_POST, ...saved, tags: Array.isArray(saved.tags) ? saved.tags : [] });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
    </AdminLayout>
  );

  const statusColor = { draft: 'bg-gray-100 text-gray-600', published: 'bg-green-100 text-green-700', scheduled: 'bg-amber-100 text-amber-700' };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto pb-24">
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/blog')} className="text-gray-500">
            <ArrowLeft className="w-4 h-4 mr-2" /> Posts
          </Button>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColor[post.status] || statusColor.draft}`}>
              {(post.status || 'draft').charAt(0).toUpperCase() + (post.status || 'draft').slice(1)}
            </span>
            <Button variant="outline" size="sm" onClick={() => save()} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            {post.status !== 'published' && (
              <Button
                size="sm"
                onClick={() => save('published')}
                disabled={saving || !post.thumbnail}
                title={!post.thumbnail ? 'Add a featured image to publish' : undefined}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <Globe className="w-4 h-4 mr-1" /> Publish
              </Button>
            )}
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex gap-0.5 border-b border-gray-200 mb-6">
          {TABS.map(({ id: tid, label, icon: Icon }) => (
            <button
              key={tid}
              onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tid ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="space-y-5">
          {/* Writing tab: featured image + title + excerpt + body + CTAs */}
          {activeTab === 'writing' && (
            <>
              <FeaturedImageUploader
                value={post.thumbnail || ''}
                onChange={(url) => patch({ thumbnail: url })}
                onUpload={handleEditorImageInsert}
                uploading={uploading}
                storageMessage={storageStatus.message}
              />
              <Input
                value={post.title}
                onChange={(e) => {
                  patch({ title: e.target.value });
                  if (!post.slug) patch({ slug: generateSlugLocal(e.target.value) });
                }}
                placeholder="Post title…"
                className="text-2xl font-bold h-14 border-0 border-b rounded-none px-0 focus-visible:ring-0 placeholder:text-gray-300"
              />
              <Textarea
                value={post.excerpt || ''}
                onChange={(e) => patch({ excerpt: e.target.value })}
                placeholder="Short excerpt shown in post listings…"
                rows={2}
                className="resize-none border-0 border-b rounded-none px-0 focus-visible:ring-0 text-gray-600 placeholder:text-gray-300"
              />
              <RichTextEditor
                value={post.content}
                onChange={(html) => patch({ content: html })}
                onImageInsert={handleEditorImageInsert}
                uploading={uploading}
              />
              <CTABlockEditor blocks={post.cta_blocks || []} onChange={(blocks) => patch({ cta_blocks: blocks })} />
            </>
          )}

          {activeTab === 'seo' && (
            <SEOTab post={post} onChange={patch} />
          )}

          {activeTab === 'author' && (
            <AuthorTab
              post={post}
              onChange={patch}
              onAvatarUpload={(file) => handleUpload(file, 'author_avatar')}
              uploading={uploading}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsTab
              post={post}
              onChange={patch}
              categories={categories}
            />
          )}

          {activeTab === 'analytics' && (
            <AnalyticsTab postId={isNew ? null : id} post={post} />
          )}
        </div>
      </div>

      {/* ── AI Panel ── */}
      <AIPanel
        post={post}
        onApply={(html) => patch({ content: html })}
        onApplySEO={(seo) => patch({ meta_title: seo.meta_title || '', meta_description: seo.meta_description || '', slug: seo.slug || post.slug })}
      />
    </AdminLayout>
  );
};

// ── List ──────────────────────────────────────────────────────────────────────

const AdminBlogList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = async () => {
    try {
      const [postsRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/content/blog`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/content/categories?type=blog`, { headers: authHeaders() }),
      ]);
      if (postsRes.ok) setPosts(await postsRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await fetch(`${API_URL}/api/admin/content/blog/${post.id}`, { method: 'DELETE', headers: authHeaders() });
    toast({ title: 'Deleted' });
    fetchData();
  };

  const filtered = posts.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchSearch = p.title.toLowerCase().includes(q);
    const matchCat = filterCategory === 'all' || p.category_id === filterCategory;
    const matchStatus = filterStatus === 'all' || (p.status || (p.is_published ? 'published' : 'draft')) === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const getCatName = (id) => categories.find(c => c.id === id)?.name || 'Uncategorized';

  const statusBadge = (post) => {
    const s = post.status || (post.is_published ? 'published' : 'draft');
    const cfg = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-gray-100 text-gray-600',
      scheduled: 'bg-amber-100 text-amber-700',
    };
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg[s] || cfg.draft}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
            <p className="text-gray-500 mt-1">{posts.length} post{posts.length !== 1 ? 's' : ''} total</p>
          </div>
          <Button onClick={() => navigate('/admin/blog/new')} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Write Post
          </Button>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search posts…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No posts found</p>
            <Button onClick={() => navigate('/admin/blog/new')} className="mt-4">Write your first post</Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(post => (
              <Card key={post.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {post.thumbnail && <img src={post.thumbnail} alt="" className="w-28 h-20 object-cover rounded-lg flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {statusBadge(post)}
                            {post.is_featured && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Featured</span>}
                            <span className="text-xs text-gray-400">{getCatName(post.category_id)}</span>
                          </div>
                          <h3 className="font-semibold text-gray-900 leading-snug truncate">{post.title}</h3>
                          <p className="text-gray-500 text-sm mt-0.5 line-clamp-1">{post.excerpt}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/blog/${post.id}`)}>
                            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(post)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(post.created_at)}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.read_time}m</span>
                        <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{post.views ?? 0}</span>
                        <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{post.likes ?? 0}</span>
                        {post.scheduled_at && post.status === 'scheduled' && (
                          <span className="flex items-center gap-1 text-amber-600"><Calendar className="w-3.5 h-3.5" />Publishes {formatDate(post.scheduled_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ── Router ────────────────────────────────────────────────────────────────────

const AdminBlog = () => {
  const { id } = useParams();
  return id ? <AdminBlogEditor /> : <AdminBlogList />;
};

export default AdminBlog;
